import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { PageHead } from "@/pages/admin/AdminLayout";
import { MapView } from "@/components/MapView";
import { StatusBadge } from "@/components/RideStatus";
import { MessagesButton } from "@/components/RideMessages";
import { PushToggle } from "@/components/PushToggle";
import { Button } from "@/components/ui/button";

function LiveCard({ r, onFocus, onCancel }) {
  return (
    <div className="space-y-2 rounded-xl border bg-card p-4" data-testid={`live-ride-${r.id}`}>
      <div className="flex items-center justify-between gap-2"><StatusBadge status={r.status} /><span className="font-semibold">{money(r.fare)}</span></div>
      <p className="text-sm font-semibold">{r.pickup.name} → {r.dropoff.name}</p>
      <p className="text-xs text-muted-foreground">
        Rider {r.rider_name} · Driver {r.driver_name || "—"}{r.driver_vehicle ? ` (${r.driver_vehicle})` : ""}<br />
        {r.location_sharing ? `Location ON${r.driver_location ? ` · updated ${when(r.driver_location.at)}` : ""}` : "Location OFF"}{r.eta_minutes ? ` · ETA ${r.eta_minutes} min` : ""}
        <br />{r.payment_method?.replace(/_/g, " ")} · {r.payment_status}{r.discount?.code ? ` · ${r.discount.code} -${money(r.discount.amount)}` : ""}{r.unread_messages ? ` · ${r.unread_messages} unread` : ""}
      </p>
      <div className="flex flex-wrap gap-1.5">
        <MessagesButton rideId={r.id} className="h-8 text-xs" label="Message rider / driver" />
        <Button size="sm" variant="outline" onClick={() => onFocus(r)} data-testid={`live-view-${r.id}`}>View route & location</Button>
        <Button size="sm" variant="ghost" asChild><Link to={`/admin/rides?ride=${r.id}`}>Update</Link></Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onCancel(r)} data-testid={`live-cancel-${r.id}`}>Cancel</Button>
      </div>
    </div>
  );
}

function ShareLinks() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["shareLinks"], queryFn: () => api.get("/admin/share-links").then((r) => r.data) });
  const revoke = async (t) => {
    try { await api.post(`/share/${t}/revoke`); toast.success("Link revoked"); qc.invalidateQueries({ queryKey: ["shareLinks"] }); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="space-y-2 rounded-xl border bg-card p-4" data-testid="share-links">
      <h2 className="font-semibold">Shared ride links</h2>
      {!data?.length && <p className="text-sm text-muted-foreground">No links shared yet.</p>}
      {data?.slice(0, 30).map((l) => (
        <div key={l.token} className="flex items-center justify-between gap-2 text-xs" data-testid="share-link-row">
          <span>{l.rider_name} · created {when(l.created_at)} · {l.active ? `expires ${when(l.expires_at)}` : l.revoked ? "revoked" : "expired"}</span>
          {l.active && <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => revoke(l.token)} data-testid={`share-revoke-${l.token.slice(0, 8)}`}>Revoke</Button>}
        </div>
      ))}
    </section>
  );
}

export function AdminLive() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["adminLive"], queryFn: () => api.get("/admin/live").then((r) => r.data), refetchInterval: 15000 });
  const [focus, setFocus] = useState(null);
  const f = focus && data?.find((r) => r.id === focus.id);
  const cancel = async (r) => {
    const reason = window.prompt("Cancellation reason");
    if (!reason) return;
    try { await api.post(`/admin/rides/${r.id}/cancel`, { reason }); toast.success("Ride cancelled"); qc.invalidateQueries({ queryKey: ["adminLive"] }); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-5">
      <PageHead title="Live rides" sub="Assigned and active rides. Driver location shows only while a ride is en route or in progress." />
      {f && <div className="relative isolate z-0 h-72 overflow-hidden rounded-xl border" data-testid="live-map"><MapView className="absolute inset-0" pickup={f.pickup} dropoff={f.dropoff} driver={f.driver_location} route={f.route} /></div>}
      <div className="grid gap-3 md:grid-cols-2">
        {data?.map((r) => <LiveCard key={r.id} r={r} onFocus={setFocus} onCancel={cancel} />)}
        {data && !data.length && <p className="text-sm text-muted-foreground">No live rides right now.</p>}
      </div>
      <ShareLinks />
    </div>
  );
}

export function AdminMessages() {
  const [view, setView] = useState("unread");
  const { data } = useQuery({ queryKey: ["adminThreads", view], queryFn: () => api.get("/admin/messages", { params: { view } }).then((r) => r.data), refetchInterval: 10000 });
  return (
    <div className="space-y-5">
      <PageHead title="Messages" sub="Every conversation is tied to a ride." />
      <div className="flex gap-1.5">
        {["unread", "active", "upcoming", "archived"].map((v) => (
          <button key={v} type="button" onClick={() => setView(v)} data-testid={`messages-view-${v}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize ${view === v ? "border-primary bg-primary text-primary-foreground" : "border-white/15 text-muted-foreground"}`}>{v}</button>
        ))}
      </div>
      {!data?.length && <p className="text-sm text-muted-foreground">No conversations here.</p>}
      {data?.map((t) => (
        <div key={t.ride_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card p-4" data-testid={`thread-${t.ride_id}`}>
          <div className="min-w-0 text-sm">
            <p className="font-semibold">{t.rider_name}{t.driver_name ? ` · driver ${t.driver_name}` : ""} {t.unread > 0 && <span className="ml-1 rounded-full bg-neon-pink px-2 text-[10px] text-white">{t.unread}</span>}</p>
            <p className="truncate text-xs text-muted-foreground">{t.pickup} → {t.dropoff} · {t.status} · {when(t.scheduled_at)}</p>
            {t.last && <p className="truncate text-xs">{t.last.sender_name}: {t.last.text}</p>}
          </div>
          <MessagesButton rideId={t.ride_id} className="h-8 text-xs" label="Open" />
        </div>
      ))}
    </div>
  );
}

export function AdminNotifications() {
  const { data } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data), refetchInterval: 15000 });
  return (
    <div className="space-y-5">
      <PageHead title="Notifications" sub="Alerts for new requests, cancellations, applications, messages and payments." />
      <div className="rounded-xl border bg-card"><PushToggle /></div>
      {data?.items?.map((n) => (
        <div key={n.id} className="rounded-xl border bg-card p-3 text-sm" data-testid="admin-notification-item">
          <p className="font-semibold">{n.title} <span className="ml-1 text-[10px] font-normal text-muted-foreground">{n.kind}</span></p>
          <p className="text-muted-foreground">{n.body}</p><p className="text-xs text-muted-foreground">{when(n.created_at)}</p>
        </div>
      ))}
    </div>
  );
}
